import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getIsInterestOnlyOffer } from '@helpers/FormalOffers/getIsInterestOnlyOffer';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { Text } from '@iwoca/orion-ui';
import { FormattedMessage } from 'react-intl';

import { FormattedCurrency } from '../../../../../../iwIntlProvider/FormattedCurrency';
import { NeedHelpSection } from '../../NeedHelpSection/NeedHelpSection';
import { ContinueButton } from '../ContinueButton/ContinueButton';
import { RepaymentCalculatorButton } from '../RepaymentCalculatorButton/RepaymentCalculatorButton';
import {
    FeesRow,
    FullTermRow,
    InterestOnlyRepaymentRows,
    InterestRateRow,
    TotalRepaymentRow
} from '../SummaryRow/SummaryRow';

export const DesktopLoanSummary = ({
    selectedOffer,
    drawdownAmount,
    monthlyRepayment,
    isLoading,
    isSingleOffer
}: {
    selectedOffer: TFormalOffer | undefined;
    drawdownAmount: number | undefined;
    monthlyRepayment: number | null;
    isLoading: boolean;
    isSingleOffer: boolean;
}) => {
    const isInterestOnly = selectedOffer
        ? getIsInterestOnlyOffer(selectedOffer)
        : false;

    return (
        <div className="relative grow basis-0 bg-primary-10 text-white">
            {/* Offset by navbar height (72px) */}
            <div className="sticky bottom-0 top-[72px] flex min-h-[calc(100dvh-72px)] max-w-[472px] flex-col p-2xl pt-5xl">
                {isSingleOffer && selectedOffer && (
                    <Text className="mb-s text-xl font-bold text-structure-100">
                        <FormattedMessage
                            id="_.loan_summary.single_offer_title_desktop"
                            defaultMessage="Your repayments over {months} months"
                            values={{
                                months: getDurationInMonths(
                                    selectedOffer.duration!
                                )
                            }}
                        />
                    </Text>
                )}

                {isInterestOnly ? (
                    <div className="flex flex-col gap-m">
                        <InterestOnlyRepaymentRows
                            selectedOffer={selectedOffer}
                            drawdownAmount={drawdownAmount}
                        />
                    </div>
                ) : (
                    <>
                        <Text className="text-m font-reg text-structure-100">
                            <FormattedMessage
                                id="_.loan_summary.min_monthly_repayment"
                                defaultMessage="Min. monthly repayment"
                            />
                        </Text>
                        <Text className="text-m font-med text-structure-100">
                            <FormattedMessage
                                id="_.loan_summary.min_monthly_repayment_desktop"
                                defaultMessage="{amount} per month"
                                values={{
                                    amount: (
                                        <span className="text-3xl font-bold">
                                            {monthlyRepayment !== null ? (
                                                <FormattedCurrency
                                                    value={monthlyRepayment}
                                                />
                                            ) : (
                                                '£--.--'
                                            )}
                                        </span>
                                    )
                                }}
                            />
                        </Text>
                    </>
                )}

                <div
                    className={`mt-l flex flex-col gap-m border-t border-secondary-20 pt-l ${isLoading ? 'opacity-50' : ''}`}
                >
                    <FullTermRow
                        selectedOffer={selectedOffer}
                        drawdownAmount={drawdownAmount}
                    />
                    <InterestRateRow
                        selectedOffer={selectedOffer}
                        drawdownAmount={drawdownAmount}
                    />
                    <FeesRow
                        selectedOffer={selectedOffer}
                        drawdownAmount={drawdownAmount}
                    />
                    <TotalRepaymentRow
                        selectedOffer={selectedOffer}
                        drawdownAmount={drawdownAmount}
                    />
                </div>

                <RepaymentCalculatorButton
                    selectedOffer={selectedOffer}
                    drawdownAmount={drawdownAmount}
                    variant="desktop"
                />

                <div className="mt-xl">
                    <ContinueButton
                        selectedOffer={selectedOffer}
                        drawdownAmount={drawdownAmount}
                    />
                </div>

                <div className="mt-5xl">
                    <NeedHelpSection theme="dark" />
                </div>
            </div>
        </div>
    );
};
